import { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Check,
  FileSignature,
  MessagesSquare,
  Send,
  type LucideIcon,
} from "lucide-react";
import { useI18n } from "@project/lib/use-i18n";
import { cn } from "@project/lib/utils";
import { Stagger, StaggerItem } from "@project/user/components/motion";

const STEPS: { n: 1 | 2 | 3 | 4; icon: LucideIcon }[] = [
  { n: 1, icon: Send },
  { n: 2, icon: MessagesSquare },
  { n: 3, icon: FileSignature },
  { n: 4, icon: BarChart3 },
];

const STEP_MS = 2600;
const LAST = STEPS.length - 1;

/**
 * "How it works" timeline: once scrolled into view, steps light up one after
 * another and loop. Hover pauses it, clicking a step jumps to it.
 */
export function CsrSteps() {
  const { t } = useI18n();
  const rootRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [inView, setInView] = useState(false);
  // Tracks draw themselves the first time the timeline scrolls into view.
  const [seen, setSeen] = useState(false);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    setReducedMotion(
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false
    );
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (inView) {
      setSeen(true);
    }
  }, [inView]);

  useEffect(() => {
    if (!inView || paused || reducedMotion) {
      return;
    }
    const timer = window.setTimeout(
      () => setActive(prev => (prev >= LAST ? 0 : prev + 1)),
      // Linger a little longer on the final step before looping.
      active === LAST ? STEP_MS * 1.4 : STEP_MS
    );
    return () => window.clearTimeout(timer);
  }, [active, inView, paused, reducedMotion]);

  // Reduced motion: show the whole path as complete, no cycling.
  const current = reducedMotion ? LAST : active;
  const progress = `${(current / LAST) * 100}%`;

  return (
    <div
      ref={rootRef}
      className="relative mt-12"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Desktop track (runs between the first and last circle centres) */}
      <div
        className={cn(
          "absolute left-[12.5%] right-[12.5%] top-7 hidden h-1 origin-left rounded-full bg-border transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] lg:block",
          seen ? "scale-x-100" : "scale-x-0"
        )}
        aria-hidden
      >
        <div
          className="h-full rounded-full bg-gradient-to-r from-primary to-gold transition-[width] duration-700 ease-out"
          style={{ width: progress }}
        />
      </div>
      {/* Mobile track (vertical) */}
      <div
        className={cn(
          "absolute bottom-7 left-7 top-7 w-1 origin-top rounded-full bg-border transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] lg:hidden",
          seen ? "scale-y-100" : "scale-y-0"
        )}
        aria-hidden
      >
        <div
          className="w-full rounded-full bg-gradient-to-b from-primary to-gold transition-[height] duration-700 ease-out"
          style={{ height: progress }}
        />
      </div>

      <Stagger
        as="ol"
        step={140}
        delay={200}
        className="relative grid gap-6 lg:grid-cols-4 lg:gap-0"
      >
        {STEPS.map(({ n, icon: Icon }, i) => {
          const done = i < current;
          const isActive = i === current;
          return (
            <StaggerItem
              as="li"
              key={n}
              index={i}
              className="flex gap-4 lg:flex-col lg:items-center lg:gap-0 lg:px-3"
            >
              <button
                type="button"
                onClick={() => setActive(i)}
                aria-current={isActive ? "step" : undefined}
                aria-label={t(`csr.process.step${n}.title`)}
                className={cn(
                  "relative grid h-14 w-14 shrink-0 place-items-center rounded-full border-4 border-background font-serif text-lg font-bold shadow-md transition-all duration-500",
                  isActive &&
                    "shadow-gold/40 scale-110 bg-gold text-gold-foreground",
                  done && "bg-primary text-primary-foreground",
                  !isActive &&
                    !done &&
                    "bg-card text-muted-foreground ring-2 ring-border"
                )}
              >
                {isActive && !reducedMotion && (
                  <span
                    className="bg-gold/40 absolute inset-0 animate-ping rounded-full"
                    aria-hidden
                  />
                )}
                <span className="relative">
                  {done ? (
                    <Check className="h-6 w-6" />
                  ) : (
                    <Icon className="h-6 w-6" />
                  )}
                </span>
              </button>

              <div
                onClick={() => setActive(i)}
                className={cn(
                  "relative flex-1 cursor-pointer overflow-hidden rounded-2xl border bg-card p-5 text-left transition-all duration-500 lg:mt-6 lg:w-full lg:text-center",
                  isActive
                    ? "border-primary/30 shadow-primary/10 shadow-xl lg:-translate-y-1"
                    : "border-border shadow-sm",
                  !isActive && !done && "opacity-70"
                )}
              >
                <span
                  className={cn(
                    "absolute inset-x-0 top-0 h-1 origin-left bg-gradient-to-r from-primary to-gold transition-transform duration-500",
                    isActive || done ? "scale-x-100" : "scale-x-0"
                  )}
                  aria-hidden
                />
                <p
                  className={cn(
                    "text-[11px] font-bold uppercase tracking-[0.2em] transition-colors",
                    isActive ? "text-gold" : "text-primary"
                  )}
                >
                  {t("csr.process.stepLabel")} {n}
                </p>
                <h3 className="mt-1.5 font-serif text-xl font-semibold leading-snug text-foreground">
                  {t(`csr.process.step${n}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {t(`csr.process.step${n}.desc`)}
                </p>
              </div>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  );
}
